import React, { useEffect, useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  PieChart, Pie, Cell,
} from "recharts";
import { Briefcase, Users, TrendingUp } from "lucide-react";
import { api } from "@/lib/api";
import { formatINR, formatPct, formatCount } from "@/utils/formatters";
import PageHeader from "@/components/layout/PageHeader";
import GlassCard from "@/components/ui-fleet/GlassCard";
import ClickableKPITile from "@/components/ui-fleet/ClickableKPITile";
import DrillDownPanel from "@/components/ui-fleet/DrillDownPanel";
import StatTableDrillDown from "@/components/ui-fleet/StatTableDrillDown";
import EngineCareCard from "@/components/ui-fleet/EngineCareCard";

const PRODUCT_COLORS = {
  BACKHOE: "#C4622D",
  EXCAVATOR: "#E8884A",
  TELEHANDLER: "#B07D1A",
  WHEEL_LOADER: "#2D7A4A",
  MINI_EXCAVATOR: "#2A5FA8",
};

const SEGMENT_COLORS = ["#C4622D", "#E8884A", "#B07D1A", "#2D7A4A", "#2A5FA8", "#7A726A"];

export default function SalesCRM() {
  const [summary, setSummary] = useState(null);
  const [dealers, setDealers] = useState([]);
  const [drill, setDrill] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/sales/summary"),
      api.get("/sales/dealer-performance"),
    ]).then(([s, d]) => {
      setSummary(s.data);
      setDealers(d.data);
    });
  }, []);

  if (!summary) return <div><PageHeader title="Sales & CRM" breadcrumb="Commercial" /><div className="text-center py-20" style={{ color: "var(--slate-500)" }}>Loading…</div></div>;

  const k = summary.kpis;
  const productGroups = Object.keys(PRODUCT_COLORS);

  return (
    <div data-testid="sales-crm">
      <PageHeader title="Sales & CRM" breadcrumb="Commercial" />

      <div className="grid grid-cols-5 gap-4 mb-5">
        <ClickableKPITile label="Fleet Value" value={formatINR(k.total_fleet_value, { compact: true })} sub="Tap for top dealers" color="copper" onClick={() => setDrill("value")} testId="kpi-fleet-value" />
        <ClickableKPITile label="Avg Price" value={formatINR(k.avg_machine_price, { compact: true })} sub="Tap for region split" color="slate" onClick={() => setDrill("avg-price")} testId="kpi-avg-price" />
        <ClickableKPITile label="Total GST" value={formatINR(k.total_gst, { compact: true })} sub="Tap for dealer GST" color="amber" onClick={() => setDrill("gst")} testId="kpi-gst" />
        <ClickableKPITile label="Net COB" value={formatINR(k.total_net_cob, { compact: true })} sub="Tap for top COB dealers" color="green" onClick={() => setDrill("cob")} testId="kpi-cob" />
        <ClickableKPITile label="Discount Given" value={formatINR(k.total_discount, { compact: true })} sub="Tap for biggest discounts" color="red" onClick={() => setDrill("discount")} testId="kpi-discount" />
      </div>

      <div className="grid grid-cols-12 gap-5 mb-5">
        <GlassCard className="col-span-7 p-5">
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp size={16} style={{ color: "var(--accent)" }} />
            <h3 className="font-display text-[16px]" style={{ color: "var(--slate-900)" }}>Revenue by Region</h3>
          </div>
          <p className="text-[12px] mb-3" style={{ color: "var(--slate-500)" }}>Stacked by product group</p>
          <div style={{ height: 290 }}>
            <ResponsiveContainer>
              <BarChart data={summary.region_data}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(184,175,164,0.25)" />
                <XAxis dataKey="region" />
                <YAxis tickFormatter={(v) => formatINR(v, { compact: true })} />
                <Tooltip formatter={(v) => formatINR(v, { compact: true })} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {productGroups.map((pg, i) => (
                  <Bar key={pg} dataKey={pg} stackId="r" fill={PRODUCT_COLORS[pg]} radius={i === productGroups.length - 1 ? [6,6,0,0] : 0} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </GlassCard>

        {/* Pipeline funnel */}
        <GlassCard className="col-span-5 p-5">
          <div className="flex items-center gap-2 mb-1">
            <Briefcase size={16} style={{ color: "var(--accent)" }} />
            <h3 className="font-display text-[16px]" style={{ color: "var(--slate-900)" }}>Sales Pipeline Funnel</h3>
          </div>
          <p className="text-[12px] mb-3" style={{ color: "var(--slate-500)" }}>Opportunity → WON</p>
          <Funnel data={summary.pipeline} />
          <div className="mt-3 grid grid-cols-3 gap-2 text-[10px]">
            {summary.source_attribution.slice(0, 5).map((s) => (
              <div key={s.campaign} className="glass-card-sm p-2 text-center">
                <div style={{ color: "var(--slate-500)" }}>{s.campaign}</div>
                <div className="font-mono" style={{ color: "var(--slate-900)" }}>{s.count}</div>
              </div>
            ))}
          </div>
        </GlassCard>
      </div>

      {/* Dealer table */}
      <GlassCard className="p-5 mb-5 overflow-x-auto" data-testid="dealer-table">
        <h3 className="font-display text-[16px] mb-3" style={{ color: "var(--slate-900)" }}>Dealer Performance</h3>
        <table className="w-full text-[12px]">
          <thead>
            <tr style={{ color: "var(--slate-500)" }}>
              {["Code", "Dealer", "Branch", "Machines", "Revenue", "Avg Disc %", "Repeat %", "Net COB", "Campaigns"].map((h) => (
                <th key={h} className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-medium">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {dealers.map((d) => (
              <tr key={d.dealer_code} className="border-t hover:bg-white/40" style={{ borderColor: "var(--glass-border-btm)" }}>
                <td className="px-3 py-2.5 font-mono">{d.dealer_code}</td>
                <td className="px-3 py-2.5 font-medium">{d.dealer_name}</td>
                <td className="px-3 py-2.5">{d.branch}</td>
                <td className="px-3 py-2.5 font-mono">{d.machines_sold}</td>
                <td className="px-3 py-2.5 font-mono" style={{ color: "var(--accent)" }}>{formatINR(d.revenue, { compact: true })}</td>
                <td className="px-3 py-2.5 font-mono">{d.avg_discount_pct}%</td>
                <td className="px-3 py-2.5 font-mono">{d.repeat_pct}%</td>
                <td className="px-3 py-2.5 font-mono">{formatINR(d.net_cob, { compact: true })}</td>
                <td className="px-3 py-2.5 text-[11px]" style={{ color: "var(--slate-500)" }}>{d.campaigns}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </GlassCard>

      {/* Customer segments */}
      <div className="grid grid-cols-4 gap-4 mb-5">
        <SegmentCard title="By Industry" icon={Users} data={summary.segments.industry} />
        <SegmentCard title="By Retail Type" data={summary.segments.retail_type} />
        <SegmentCard title="By NGR" data={summary.segments.ngr} />
        <SegmentCard title="By Financier" data={summary.segments.financier} />
      </div>

      {/* Engine Care Revenue Recovery */}
      <EngineCareCard />

      <DrillDownPanel
        isOpen={drill !== null}
        onClose={() => setDrill(null)}
        title={
          drill === "value" ? "Fleet Revenue — Top Dealers"
          : drill === "avg-price" ? "Average Price by Region"
          : drill === "gst" ? "GST Contribution by Dealer"
          : drill === "cob" ? "Net COB Leaders"
          : drill === "discount" ? "Biggest Discounts Given"
          : ""
        }
        subtitle={
          drill === "value" ? `Total fleet value ${formatINR(k.total_fleet_value, { compact: true })}`
          : drill === "avg-price" ? `Fleet avg ${formatINR(k.avg_machine_price, { compact: true })}`
          : drill === "gst" ? `Total GST ${formatINR(k.total_gst, { compact: true })}`
          : drill === "cob" ? `Total net COB ${formatINR(k.total_net_cob, { compact: true })}`
          : drill === "discount" ? `Total discount ${formatINR(k.total_discount, { compact: true })}`
          : ""
        }
        testId="sales-drilldown"
      >
        {drill === "value" && (
          <StatTableDrillDown
            rows={[...dealers].sort((a, b) => b.revenue - a.revenue)}
            columns={[
              { key: "dealer_name", label: "Dealer" },
              { key: "machines_sold", label: "Units", format: (v) => formatCount(v) },
              { key: "revenue", label: "Revenue", format: (v) => formatINR(v, { compact: true }) },
              { key: "repeat_pct", label: "Repeat %", format: (v) => formatPct(v) },
            ]}
          />
        )}

        {drill === "avg-price" && (
          <StatTableDrillDown
            rows={summary.region_data.map((r) => {
              const totalUnitsRevenue = productGroups.reduce((s, pg) => s + (r[pg] || 0), 0);
              return { region: r.region, revenue: totalUnitsRevenue };
            })}
            columns={[
              { key: "region", label: "Region" },
              { key: "revenue", label: "Revenue", format: (v) => formatINR(v, { compact: true }) },
            ]}
          />
        )}

        {drill === "gst" && (
          <StatTableDrillDown
            rows={[...dealers].sort((a, b) => b.revenue - a.revenue).map((d) => ({
              ...d,
              gst_est: Math.round(d.revenue * 0.18 / 1.18),
            }))}
            columns={[
              { key: "dealer_name", label: "Dealer" },
              { key: "machines_sold", label: "Units" },
              { key: "gst_est", label: "GST (est.)", format: (v) => formatINR(v, { compact: true }) },
            ]}
          />
        )}

        {drill === "cob" && (
          <StatTableDrillDown
            rows={[...dealers].sort((a, b) => b.net_cob - a.net_cob)}
            columns={[
              { key: "dealer_name", label: "Dealer" },
              { key: "machines_sold", label: "Units" },
              { key: "net_cob", label: "Net COB", format: (v) => formatINR(v, { compact: true }) },
              { key: "avg_discount_pct", label: "Avg Disc %", format: (v) => formatPct(v) },
            ]}
          />
        )}

        {drill === "discount" && (
          <StatTableDrillDown
            rows={[...dealers].sort((a, b) => b.avg_discount_pct - a.avg_discount_pct)}
            columns={[
              { key: "dealer_name", label: "Dealer" },
              { key: "machines_sold", label: "Units" },
              { key: "avg_discount_pct", label: "Avg Disc %", format: (v) => formatPct(v) },
              { key: "revenue", label: "Revenue", format: (v) => formatINR(v, { compact: true }) },
            ]}
          />
        )}
      </DrillDownPanel>
    </div>
  );
}

function Funnel({ data }) {
  return (
    <div className="space-y-2">
      {data.map((s, i) => {
        const pct = s.pct;
        return (
          <div key={s.stage}>
            <div className="flex justify-between text-[11px] mb-1">
              <span style={{ color: "var(--slate-700)" }}>{s.stage}</span>
              <span className="font-mono" style={{ color: "var(--slate-500)" }}>{s.value} · {pct}%</span>
            </div>
            <div className="h-7 rounded-md overflow-hidden" style={{ background: "rgba(184,175,164,0.18)" }}>
              <div
                className="h-full flex items-center justify-end pr-2 text-[10px] text-white font-medium"
                style={{
                  width: `${pct}%`,
                  background: `linear-gradient(90deg, var(--accent-light) 0%, var(--accent) 100%)`,
                  opacity: 1 - i * 0.13,
                }}
              >
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function SegmentCard({ title, icon: Icon, data }) {
  return (
    <GlassCard variant="sm" className="p-5">
      <h4 className="font-display text-[15px] mb-3 flex items-center gap-2" style={{ color: "var(--slate-900)" }}>
        {Icon && <Icon size={14} style={{ color: "var(--accent)" }} />} {title}
      </h4>
      <div style={{ height: 140 }}>
        <ResponsiveContainer>
          <PieChart>
            <Pie data={data} dataKey="count" innerRadius={32} outerRadius={56} paddingAngle={2}>
              {data.map((d, i) => <Cell key={i} fill={SEGMENT_COLORS[i % SEGMENT_COLORS.length]} />)}
            </Pie>
            <Tooltip />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div className="space-y-1 mt-2">
        {data.slice(0, 5).map((d, i) => (
          <div key={d.name} className="flex items-center text-[10px] gap-1.5">
            <span className="w-2 h-2 rounded-full" style={{ background: SEGMENT_COLORS[i % SEGMENT_COLORS.length] }} />
            <span className="flex-1 truncate" style={{ color: "var(--slate-700)" }}>{d.name}</span>
            <span className="font-mono" style={{ color: "var(--slate-900)" }}>{d.count}</span>
          </div>
        ))}
      </div>
    </GlassCard>
  );
}
